import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import ConfirmModal from '../ConfirmModal'
import { Building2, Eye, EyeOff, Plus, RefreshCw, X } from 'lucide-react'

const EMPTY_MEMBER = { full_name: '', email: '', password: '', permission_set_id: '', business_unit_ids: [] }

function getInitials(name, email) {
  const source = name?.trim() || email?.split('@')[0] || ''
  if (!source) return '?'

  const parts = source.split(/[ ._-]/).filter(Boolean)
  return parts.slice(0, 2).map(p => p[0]?.toUpperCase()).join('')
}

function generateTemporaryPassword() {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*'
  const values = new Uint32Array(14)
  window.crypto.getRandomValues(values)
  return `Dl1!${Array.from(values, value => characters[value % characters.length]).join('')}`
}

export default function Team() {
  const toast = useToast()
  const { user, org, orgId, isAdmin, activeCrmWorkspaceId, refreshProfile } = useUser()
  const [profiles, setProfiles] = useState([])
  const [invites, setInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [permissionSets, setPermissionSets] = useState([])
  const [invitePermissionSetId, setInvitePermissionSetId] = useState('')
  const [copiedToken, setCopiedToken] = useState(null)
  const [confirmRevoke, setConfirmRevoke] = useState(null)
  const [showMemberModal, setShowMemberModal] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [creatingMember, setCreatingMember] = useState(false)
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER)
  const [businessUnits, setBusinessUnits] = useState([])
  const [businessMemberships, setBusinessMemberships] = useState([])
  const [businessAccessMember, setBusinessAccessMember] = useState(null)
  const [selectedBusinessUnitIds, setSelectedBusinessUnitIds] = useState([])
  const [savingBusinessAccess, setSavingBusinessAccess] = useState(false)
  const [showBusinessForm, setShowBusinessForm] = useState(false)
  const [businessName, setBusinessName] = useState('')
  const [creatingBusiness, setCreatingBusiness] = useState(false)

  useEffect(() => {
    if (!orgId) return

    fetchProfiles()
    fetchInvites()
    fetchPermissionSets()
    fetchBusinessAccess()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  async function fetchProfiles() {
    setError(null)

    const { data, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at')

    if (fetchError) {
      setError('Failed to load team members. Please refresh and try again.')
      setLoading(false)
      return
    }

    setProfiles(data || [])
    setLoading(false)
  }

  async function fetchInvites() {
    if (!orgId) {
      setInvites([])
      return
    }

    const { data, error: inviteError } = await supabase
      .from('invites')
      .select('*')
      .eq('org_id', orgId)
      .is('accepted_at', null)
      .order('created_at', { ascending: false })

    if (inviteError) {
      toast.error('Failed to load pending invites')
      setInvites([])
      return
    }

    setInvites(data || [])
  }

  async function fetchPermissionSets() {
    const { data, error: permissionError } = await supabase
      .from('permission_sets')
      .select('id, name, description, is_system')
      .eq('org_id', orgId)
      .order('name')

    if (permissionError) {
      toast.error('Failed to load permission sets')
      return
    }

    const nextSets = data || []
    setPermissionSets(nextSets)
    setInvitePermissionSetId(current => current || nextSets.find(set => set.name === 'Standard Access')?.id || nextSets[0]?.id || '')
    setMemberForm(current => ({
      ...current,
      permission_set_id: current.permission_set_id || nextSets.find(set => set.name === 'Standard Access')?.id || nextSets[0]?.id || ''
    }))
  }

  async function fetchBusinessAccess() {
    const [{ data: unitData, error: unitError }, { data: membershipData, error: membershipError }] = await Promise.all([
      supabase.from('business_units').select('id, name, is_default').eq('org_id', orgId).eq('archived', false).order('name'),
      supabase.from('business_unit_memberships').select('business_unit_id, user_id, role').eq('org_id', orgId)
    ])

    if (unitError || membershipError) {
      console.error('Failed to load Business access:', unitError || membershipError)
      toast.error('Failed to load Business access')
      return
    }

    setBusinessUnits(unitData || [])
    setBusinessMemberships(membershipData || [])
  }

  function openBusinessAccess(profile) {
    setBusinessAccessMember(profile)
    setSelectedBusinessUnitIds(
      businessMemberships
        .filter(membership => membership.user_id === profile.id)
        .map(membership => membership.business_unit_id)
    )
  }

  function toggleBusinessUnit(businessUnitId) {
    setSelectedBusinessUnitIds(current => current.includes(businessUnitId)
      ? current.filter(id => id !== businessUnitId)
      : [...current, businessUnitId])
  }

  async function saveBusinessAccess() {
    if (!businessAccessMember) return
    setSavingBusinessAccess(true)
    const { error: saveError } = await supabase.rpc('replace_business_unit_memberships', {
      target_user_id: businessAccessMember.id,
      target_business_unit_ids: selectedBusinessUnitIds
    })
    setSavingBusinessAccess(false)

    if (saveError) {
      console.error('Failed to update Business access:', saveError)
      toast.error(saveError.message || 'Failed to update Business access')
      return
    }

    toast.success(`Business access updated for ${businessAccessMember.email}`)
    setBusinessAccessMember(null)
    fetchBusinessAccess()
  }

  async function createBusiness(event) {
    event.preventDefault()
    const name = businessName.trim()
    if (!name) return toast.error('Business name is required')

    setCreatingBusiness(true)
    const query = activeCrmWorkspaceId
      ? supabase.from('crm_businesses').insert({ workspace_id: activeCrmWorkspaceId, name, created_by: user.id })
      : supabase.from('business_units').insert({ org_id: orgId, name })
    const { error: createError } = await query
    setCreatingBusiness(false)

    if (createError) {
      return toast.error(createError.code === '23505' ? 'That Business already exists' : createError.message || 'Failed to create Business')
    }

    setBusinessName('')
    setShowBusinessForm(false)
    toast.success('Business created')
    await refreshProfile({ authUser: user })
    fetchBusinessAccess()
  }

  function openMemberModal() {
    setMemberForm({
      ...EMPTY_MEMBER,
      password: generateTemporaryPassword(),
      permission_set_id: permissionSets.find(set => set.name === 'Standard Access')?.id || permissionSets[0]?.id || ''
    })
    setShowPassword(false)
    setShowMemberModal(true)
  }

  function toggleNewMemberBusinessUnit(businessUnitId) {
    setMemberForm(current => ({
      ...current,
      business_unit_ids: current.business_unit_ids.includes(businessUnitId)
        ? current.business_unit_ids.filter(id => id !== businessUnitId)
        : [...current.business_unit_ids, businessUnitId]
    }))
  }

  async function createMember(event) {
    event.preventDefault()

    if (!memberForm.full_name.trim() || !memberForm.email.trim() || !memberForm.permission_set_id) {
      toast.error('Full name, email and permission set are required')
      return
    }
    if (memberForm.password.length < 12) {
      toast.error('Temporary password must be at least 12 characters')
      return
    }

    setCreatingMember(true)
    const { data, error: createError } = await supabase.functions.invoke('create-organisation-member', {
      body: {
        full_name: memberForm.full_name.trim(),
        email: memberForm.email.trim().toLowerCase(),
        password: memberForm.password,
        permission_set_id: memberForm.permission_set_id,
        business_unit_ids: memberForm.business_unit_ids
      }
    })
    setCreatingMember(false)

    if (createError || data?.error) {
      let message = data?.error

      if (!message && createError?.context) {
        try {
          const errorBody = await createError.context.json()
          message = errorBody?.error || errorBody?.message || errorBody?.msg
        } catch {
          // Fall back to the client error when the response is not JSON.
        }
      }

      toast.error(message || createError?.message || 'Failed to add team member')
      return
    }

    setShowMemberModal(false)
    setMemberForm(EMPTY_MEMBER)
    toast.success('Team member added')
    fetchProfiles()
  }

  async function sendInvite() {
    if (!orgId || !user?.id) {
      toast.error('Your organisation is still loading')
      return
    }

    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }

    const normalizedEmail = inviteEmail.trim().toLowerCase()

    const alreadyMember = profiles.some(p => p.email?.toLowerCase() === normalizedEmail)
    if (alreadyMember) {
      toast.error('This person is already a team member')
      return
    }

    const alreadyInvited = invites.some(i => i.email?.toLowerCase() === normalizedEmail)
    if (alreadyInvited) {
      toast.error('An invite has already been sent to this address')
      return
    }

    setInviting(true)

    const { data, error } = await supabase
      .from('invites')
      .insert([{
        org_id: orgId,
        email: normalizedEmail,
        role: 'member',
        permission_set_id: invitePermissionSetId || null,
        invited_by: user.id
      }])
      .select()
      .single()

    setInviting(false)

    if (error) {
      toast.error('Failed to create invite. Please try again.')
      return
    }

    toast.success(`Invite created for ${normalizedEmail}`)
    setInviteEmail('')
    fetchInvites()

    const link = `${window.location.origin}/accept-invite?token=${data.token}`
    navigator.clipboard.writeText(link).then(() => {
      setCopiedToken(data.token)
      setTimeout(() => setCopiedToken(null), 4000)
    })
  }

  async function revokeInvite(id) {
    const { error } = await supabase
      .from('invites')
      .delete()
      .eq('id', id)
      .eq('org_id', orgId)

    if (error) {
      toast.error('Failed to revoke invite.')
    } else {
      setInvites(prev => prev.filter(invite => invite.id !== id))
      toast.success('Invite revoked')
    }

    setConfirmRevoke(null)
  }

  async function updateAccess(id, permissionSetId, email) {
    const permissionSet = permissionSets.find(set => set.id === permissionSetId)
    const role = permissionSet?.name === 'Organisation Admin' ? 'admin' : 'member'

    const { error } = await supabase
      .from('profiles')
      .update({ role, permission_set_id: permissionSetId })
      .eq('id', id)
      .eq('org_id', orgId)

    if (error) {
      toast.error('Failed to update access. Please try again.')
    } else {
      toast.success(`Access updated for ${email}`)
    }

    fetchProfiles()
  }

  function copyInviteLink(token) {
    const link = `${window.location.origin}/accept-invite?token=${token}`

    navigator.clipboard.writeText(link).then(() => {
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(null), 2000)
    })
  }

  const inviteToRevoke = confirmRevoke ? invites.find(i => i.id === confirmRevoke) : null

  return (
    <div className="hv-page" style={{ maxWidth: '760px' }}>

      {/* Header */}
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Team</h1>
          <p className="hv-page-subtitle">
            {org?.name ? `${org.name} · ` : ''}{profiles.length} {profiles.length === 1 ? 'member' : 'members'}
          </p>
        </div>
        {isAdmin && <div style={{ display: 'flex', gap: 10 }}>
          <button className="hv-btn hv-btn-secondary" onClick={() => setShowBusinessForm(true)}>
            <Building2 size={15} />
            New Business
          </button>
          <button className="hv-btn hv-btn-primary" onClick={openMemberModal}>
            <Plus size={15} />
            Add Member
          </button>
        </div>}
      </div>

      {showBusinessForm && (
        <div className="hv-modal-overlay" onMouseDown={() => !creatingBusiness && setShowBusinessForm(false)}>
          <form className="hv-modal" style={{ maxWidth: 430 }} onSubmit={createBusiness} onMouseDown={event => event.stopPropagation()}>
            <div className="hv-modal-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
              <div>
                <div className="hv-modal-title">Create Business</div>
                <p style={{ margin: '5px 0 0', color: 'var(--hv-text-secondary)', fontSize: 13, lineHeight: 1.45 }}>
                  Add a separate area for its clients, deals, projects and CRM records.
                </p>
              </div>
              <button type="button" className="hv-btn hv-btn-ghost" onClick={() => setShowBusinessForm(false)} disabled={creatingBusiness} title="Close" style={{ width: 30, height: 30, minHeight: 30, padding: 0, flexShrink: 0 }}>
                <X size={17} />
              </button>
            </div>
            <div className="hv-modal-body">
              <label className="hv-form-label">Business Name</label>
              <input className="hv-input" autoFocus value={businessName} onChange={event => setBusinessName(event.target.value)} placeholder="e.g. Events" />
            </div>
            <div className="hv-modal-footer" style={{ borderTop: '1px solid var(--hv-border)', paddingTop: 16 }}>
              <button type="button" className="hv-btn hv-btn-secondary" onClick={() => setShowBusinessForm(false)} disabled={creatingBusiness}>Cancel</button>
              <button type="submit" className="hv-btn hv-btn-primary" disabled={creatingBusiness}>{creatingBusiness ? 'Creating...' : 'Create Business'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Invite */}
      {false && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--hv-text-primary)', marginBottom: '6px' }}>
            Invite a team member
          </div>

          <p style={{ fontSize: '13px', color: 'var(--hv-text-muted)', marginBottom: '16px' }}>
            An invite link will be generated. Copy and send it to them directly.
            {' '}<span style={{ color: 'var(--hv-amber)', fontWeight: '500' }}>Email sending will be enabled once a domain is connected.</span>
          </p>

          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              className="hv-input"
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendInvite()}
              style={{ flex: 1 }}
            />

            <button
              className="hv-btn hv-btn-primary"
              onClick={sendInvite}
              disabled={inviting}
              style={{ whiteSpace: 'nowrap' }}
            >
              {inviting ? 'Creating...' : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Create Invite
                </>
              )}
            </button>
          </div>

          <div style={{ marginTop: 12 }}>
            <label className="hv-form-label">Permission Set</label>
            <select className="hv-select" value={invitePermissionSetId} onChange={event => setInvitePermissionSetId(event.target.value)}>
              {permissionSets.filter(set => set.name !== 'Organisation Admin').map(set => (
                <option key={set.id} value={set.id}>{set.name}</option>
              ))}
            </select>
          </div>

          {copiedToken && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: 'var(--hv-green-bg)',
              border: '1px solid #86efac',
              borderRadius: 'var(--hv-radius-sm)',
              fontSize: '13px',
              color: 'var(--hv-green-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Invite link copied to clipboard
            </div>
          )}
        </div>
      )}

      {/* Pending invites */}
      {false && invites.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: 'var(--hv-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '10px'
          }}>
            Pending Invites
          </div>

          <div className="hv-table-wrap" style={{ overflow: 'hidden' }}>
            {invites.map((invite, i) => (
              <div key={invite.id} style={{
                padding: '13px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                borderBottom: i < invites.length - 1 ? '1px solid var(--hv-border)' : 'none'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'var(--hv-amber-bg)',
                  border: '1px solid var(--hv-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--hv-amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13.5px', fontWeight: '500', color: 'var(--hv-text-primary)' }}>{invite.email}</div>
                  <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginTop: '2px' }}>
                    Invited {new Date(invite.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>

                <span className="hv-badge hv-badge-amber">Pending</span>

                <button
                  className="hv-btn hv-btn-ghost"
                  style={{ fontSize: '12px', color: 'var(--hv-accent)' }}
                  onClick={() => copyInviteLink(invite.token)}
                >
                  {copiedToken === invite.token ? 'Copied!' : 'Copy Link'}
                </button>

                <button
                  className="hv-btn hv-btn-danger"
                  style={{ fontSize: '12px' }}
                  onClick={() => setConfirmRevoke(invite.id)}
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background: 'var(--hv-red-bg)',
          border: '1px solid #fca5a5',
          borderRadius: 'var(--hv-radius)',
          padding: '14px 18px',
          color: 'var(--hv-red-text)',
          fontSize: '13.5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Team list */}
      <div className="hv-table-wrap" style={{ overflow: 'hidden' }}>
        <div style={{
          padding: '14px 22px',
          borderBottom: '1px solid var(--hv-border)',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--hv-text-primary)',
          background: '#FAFBFD',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          Team Members

          <span style={{
            background: 'var(--hv-bg)',
            border: '1px solid var(--hv-border)',
            borderRadius: '20px',
            padding: '1px 8px',
            fontSize: '11px',
            color: 'var(--hv-text-muted)',
            fontWeight: '500'
          }}>
            {profiles.length}
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '32px 22px', color: 'var(--hv-text-muted)', fontSize: '13.5px' }}>Loading...</div>
        ) : profiles.length === 0 ? (
          <div className="hv-empty">
            <div className="hv-empty-title">No team members yet</div>
            <div className="hv-empty-text">Invite your first team member above.</div>
          </div>
        ) : (
          profiles.map((p, i) => {
            const isYou = p.id === user?.id
            const isLast = i === profiles.length - 1
            const displayName = p.full_name || p.email

            return (
              <div
                key={p.id}
                style={{
                  padding: '14px 22px',
                  borderBottom: isLast ? 'none' : '1px solid var(--hv-border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px'
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: isYou ? 'rgba(79,110,247,0.15)' : 'var(--hv-bg)',
                  border: '1px solid var(--hv-border)',
                  color: isYou ? 'var(--hv-accent)' : 'var(--hv-text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '600',
                  flexShrink: 0,
                  fontFamily: 'var(--hv-mono)'
                }}>
                  {getInitials(p.full_name, p.email)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13.5px',
                    fontWeight: '500',
                    color: 'var(--hv-text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {displayName}

                    {isYou && (
                      <span style={{
                        fontSize: '11px',
                        padding: '1px 7px',
                        background: 'var(--hv-accent-light)',
                        color: 'var(--hv-accent)',
                        borderRadius: '20px',
                        fontWeight: '500'
                      }}>
                        You
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: '12px', color: 'var(--hv-text-muted)', marginTop: '2px' }}>
                    {p.full_name && p.email ? `${p.email} · ` : ''}
                    Joined {new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>

{isAdmin && !isYou ? (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    {businessUnits.length > 1 && <button
      type="button"
      className="hv-btn hv-btn-secondary"
      onClick={() => openBusinessAccess(p)}
      title="Assign Business access"
    >
      <Building2 size={14} />
      Businesses ({businessMemberships.filter(membership => membership.user_id === p.id).length})
    </button>}
    <select
      className="hv-select"
      value={p.permission_set_id || ''}
      onChange={e => updateAccess(p.id, e.target.value, p.email)}
      style={{ width: 'auto', minWidth: '190px' }}
      title="Access level"
    >
      <option value="" disabled>Select access</option>
      {permissionSets.map(set => <option key={set.id} value={set.id}>{set.name}</option>)}
    </select>

    <button
      className="hv-btn hv-btn-danger"
      style={{ fontSize: '12px' }}
      onClick={async () => {
        const confirmed = window.confirm(`Remove ${p.email} from the organisation?`)

        if (!confirmed) return

        const { error } = await supabase
          .from('profiles')
          .delete()
          .eq('id', p.id)
          .eq('org_id', orgId)

        if (error) {
          toast.error(`Failed to remove user: ${error.message}`)
        } else {
          toast.success('User removed')
          fetchProfiles()
        }
      }}
    >
      Remove
    </button>
  </div>
) : (
  <span className={`hv-badge ${p.role === 'admin' ? 'hv-badge-blue' : 'hv-badge-gray'}`}>
    {p.role === 'admin'
      ? 'Organisation Admin'
      : permissionSets.find(set => set.id === p.permission_set_id)?.name || 'Member'}
  </span>
)}
              </div>
            )
          })
        )}
      </div>

      {confirmRevoke && (
        <ConfirmModal
          title="Revoke invite"
          message={`The invite for ${inviteToRevoke?.email} will be cancelled. They will no longer be able to use the invite link.`}
          confirmLabel="Revoke Invite"
          onConfirm={() => revokeInvite(confirmRevoke)}
          onCancel={() => setConfirmRevoke(null)}
          danger={false}
        />
      )}

      {showMemberModal && (
        <div className="hv-modal-overlay" onMouseDown={() => !creatingMember && setShowMemberModal(false)}>
          <div className="hv-modal" style={{ maxWidth: 480 }} onMouseDown={event => event.stopPropagation()}>
            <div className="hv-modal-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
              <div>
                <div className="hv-modal-title">Add Team Member</div>
                <p style={{ margin: '5px 0 0', color: 'var(--hv-text-secondary)', fontSize: 13, lineHeight: 1.45 }}>
                  Add a user to {org?.name || 'your organisation'}.
                </p>
              </div>
              <button className="hv-btn hv-btn-ghost" onClick={() => setShowMemberModal(false)} disabled={creatingMember} title="Close" style={{ width: 30, height: 30, minHeight: 30, padding: 0, flexShrink: 0 }}>
                <X size={17} />
              </button>
            </div>

            <form onSubmit={createMember}>
              <div className="hv-modal-body" style={{ display: 'grid', gap: 14 }}>
                <div>
                  <label className="hv-form-label">Full Name</label>
                  <input className="hv-input" autoFocus placeholder="e.g. Alex Morgan" value={memberForm.full_name} onChange={event => setMemberForm({ ...memberForm, full_name: event.target.value })} />
                </div>
                <div>
                  <label className="hv-form-label">Email Address</label>
                  <input className="hv-input" type="email" placeholder="alex@example.com" value={memberForm.email} onChange={event => setMemberForm({ ...memberForm, email: event.target.value })} />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <label className="hv-form-label">Temporary Password</label>
                    <button
                      type="button"
                      className="hv-btn hv-btn-ghost"
                      onClick={() => setMemberForm({ ...memberForm, password: generateTemporaryPassword() })}
                      style={{ minHeight: 24, height: 24, padding: '0 5px', marginBottom: 4 }}
                      title="Generate a new temporary password"
                    >
                      <RefreshCw size={13} />
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input className="hv-input" type={showPassword ? 'text' : 'password'} placeholder="At least 12 characters" value={memberForm.password} onChange={event => setMemberForm({ ...memberForm, password: event.target.value })} style={{ paddingRight: 42 }} />
                    <button type="button" onClick={() => setShowPassword(value => !value)} title={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 0, background: 'transparent', color: 'var(--hv-text-muted)', padding: 6, cursor: 'pointer', display: 'flex' }}>
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  <div style={{ marginTop: 5, fontSize: 11.5, color: 'var(--hv-text-muted)' }}>The user can change this from their profile.</div>
                </div>
                <div>
                  <label className="hv-form-label">Access</label>
                  <select className="hv-select" value={memberForm.permission_set_id} onChange={event => setMemberForm({ ...memberForm, permission_set_id: event.target.value })}>
                    <option value="">Select access</option>
                    {permissionSets.map(set => <option key={set.id} value={set.id}>{set.name}</option>)}
                  </select>
                </div>
                {businessUnits.length > 1 && <div>
                  <label className="hv-form-label">Businesses</label>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {businessUnits.map(unit => (
                      <label key={unit.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 11px', border: '1px solid var(--hv-border)', borderRadius: 6, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={memberForm.business_unit_ids.includes(unit.id)}
                          onChange={() => toggleNewMemberBusinessUnit(unit.id)}
                        />
                        <Building2 size={15} color="var(--hv-text-muted)" />
                        <span style={{ flex: 1, fontSize: 13 }}>{unit.name}</span>
                        {unit.is_default && <span className="hv-badge hv-badge-gray">Default</span>}
                      </label>
                    ))}
                    {businessUnits.length === 0 && <div className="hv-empty-text">No Business units available.</div>}
                  </div>
                </div>}
              </div>

              <div className="hv-modal-footer" style={{ borderTop: '1px solid var(--hv-border)', paddingTop: 16 }}>
                <button type="button" className="hv-btn hv-btn-secondary" onClick={() => setShowMemberModal(false)} disabled={creatingMember}>Cancel</button>
                <button type="submit" className="hv-btn hv-btn-primary" disabled={creatingMember}>{creatingMember ? 'Adding...' : 'Add Member'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {businessAccessMember && (
        <div className="hv-modal-overlay" onMouseDown={() => !savingBusinessAccess && setBusinessAccessMember(null)}>
          <div className="hv-modal" style={{ maxWidth: 480 }} onMouseDown={event => event.stopPropagation()}>
            <div className="hv-modal-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
              <div>
                <div className="hv-modal-title">Business Access</div>
                <p style={{ margin: '5px 0 0', color: 'var(--hv-text-secondary)', fontSize: 13, lineHeight: 1.45 }}>
                  {businessAccessMember.full_name || businessAccessMember.email}
                </p>
              </div>
              <button className="hv-btn hv-btn-ghost" onClick={() => setBusinessAccessMember(null)} disabled={savingBusinessAccess} title="Close" style={{ width: 30, height: 30, minHeight: 30, padding: 0, flexShrink: 0 }}>
                <X size={17} />
              </button>
            </div>

            <div className="hv-modal-body" style={{ display: 'grid', gap: 8 }}>
              {businessUnits.map(unit => (
                <label key={unit.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', border: '1px solid var(--hv-border)', borderRadius: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedBusinessUnitIds.includes(unit.id)}
                    onChange={() => toggleBusinessUnit(unit.id)}
                  />
                  <Building2 size={16} color="var(--hv-text-muted)" />
                  <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{unit.name}</span>
                  {unit.is_default && <span className="hv-badge hv-badge-gray">Default</span>}
                </label>
              ))}
              {businessUnits.length === 0 && <div className="hv-empty-text">No Business units available.</div>}
            </div>

            <div className="hv-modal-footer" style={{ borderTop: '1px solid var(--hv-border)', paddingTop: 16 }}>
              <button type="button" className="hv-btn hv-btn-secondary" onClick={() => setBusinessAccessMember(null)} disabled={savingBusinessAccess}>Cancel</button>
              <button type="button" className="hv-btn hv-btn-primary" onClick={saveBusinessAccess} disabled={savingBusinessAccess}>
                {savingBusinessAccess ? 'Saving...' : 'Save Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
